import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { findNextSelectableSkillCommandIndex } from '../../../../lib/skill-command-utils';
import {
  groupSkillCommandsBySource,
  sortSkillCommandsBySource,
  type SkillCommand,
  type SkillCommandSourceGroup,
} from '../../../../lib/skill-constants';

interface UseSkillListStateOptions {
  commands: SkillCommand[];
  searchQuery: string;
  isOpen: boolean;
  onSelect: (command: SkillCommand) => void;
  onClose: () => void;
  inputRef?: React.RefObject<HTMLTextAreaElement | HTMLDivElement | null>;
  selectedIndex?: number;
  onSelectedIndexChange?: (index: number) => void;
  isCommandDisabled?: (command: SkillCommand) => boolean;
}

const isCommandNeverDisabled = () => false;

/**
 * Shared hook for managing slash command list state including:
 * - Filtering commands based on search query
 * - Grouping commands by source
 * - Keyboard navigation
 * - Expanded command state
 */
export function useSkillListState({
  commands,
  searchQuery,
  isOpen,
  onSelect,
  onClose,
  inputRef,
  selectedIndex: controlledSelectedIndex,
  onSelectedIndexChange,
  isCommandDisabled = isCommandNeverDisabled,
}: UseSkillListStateOptions) {
  const [uncontrolledSelectedIndex, setUncontrolledSelectedIndex] = useState(0);
  const [expandedCommandUuid, setExpandedCommandUuid] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const selectedIndex = controlledSelectedIndex ?? uncontrolledSelectedIndex;
  const selectedIndexRef = useRef(selectedIndex);

  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  const setSelectedIndex = useCallback(
    (nextIndex: number | ((currentIndex: number) => number)) => {
      const resolvedIndex = typeof nextIndex === 'function' ? nextIndex(selectedIndexRef.current) : nextIndex;

      if (onSelectedIndexChange) {
        onSelectedIndexChange(resolvedIndex);
        return;
      }

      setUncontrolledSelectedIndex(resolvedIndex);
    },
    [onSelectedIndexChange],
  );

  // Filter commands based on search query
  const filteredCommands = useMemo(() => filterSkillCommands(commands, searchQuery), [commands, searchQuery]);

  // Group filtered commands by source
  const groupedCommands = useMemo<SkillCommandSourceGroup[]>(
    () => groupSkillCommandsBySource(filteredCommands),
    [filteredCommands],
  );

  // Flatten commands for keyboard navigation
  const flatCommands = useMemo(() => filteredCommands, [filteredCommands]);

  // Reset selected index and collapse expanded command when filtered commands change
  useEffect(() => {
    setSelectedIndex(findNextSelectableSkillCommandIndex(filteredCommands, -1, 1, isCommandDisabled));
    setExpandedCommandUuid(null);
  }, [filteredCommands, isCommandDisabled, setSelectedIndex]);

  // Toggle expanded state for a command and move keyboard highlight to it.
  const toggleExpanded = useCallback(
    (commandUuid: string, index: number) => {
      setExpandedCommandUuid((prev) => (prev === commandUuid ? null : commandUuid));
      setSelectedIndex(index);
    },
    [setSelectedIndex],
  );

  // Scroll selected item into view
  useEffect(() => {
    const selectedItem = itemRefs.current.get(selectedIndex);
    if (selectedItem && listRef.current) {
      selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) => findNextSelectableSkillCommandIndex(flatCommands, prev, 1, isCommandDisabled));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => findNextSelectableSkillCommandIndex(flatCommands, prev, -1, isCommandDisabled));
          break;
        case 'Tab':
        case 'Enter':
          event.preventDefault();
          if (flatCommands[selectedIndex] && !isCommandDisabled(flatCommands[selectedIndex])) {
            onSelect(flatCommands[selectedIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          onClose();
          break;
        default:
          break;
      }
    },
    [isOpen, flatCommands, selectedIndex, onSelect, onClose, setSelectedIndex, isCommandDisabled],
  );

  // Attach keyboard listener to editor element
  useEffect(() => {
    const editor = inputRef?.current;
    if (!editor || !isOpen) return;

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (!editor.contains(event.target as Node | null)) {
        return;
      }

      handleKeyDown(event);
    };

    document.addEventListener('keydown', handleDocumentKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown, true);
    };
  }, [inputRef, isOpen, handleKeyDown]);

  // Register item ref for keyboard navigation
  const registerItemRef = useCallback((index: number, el: HTMLButtonElement | null) => {
    if (el) {
      itemRefs.current.set(index, el);
    } else {
      itemRefs.current.delete(index);
    }
  }, []);

  return {
    filteredCommands,
    groupedCommands,
    selectedIndex,
    expandedCommandUuid,
    listRef,
    toggleExpanded,
    registerItemRef,
  };
}

export function filterSkillCommands(commands: SkillCommand[], searchQuery: string): SkillCommand[] {
  if (!searchQuery || searchQuery === '/') {
    return sortSkillCommandsBySource(commands);
  }

  const normalizedQuery = searchQuery.toLowerCase().replace(/^\//, '');
  return sortSkillCommandsBySource(
    commands.filter(
      (command) =>
        command.handle.toLowerCase().includes(normalizedQuery) ||
        command.title?.toLowerCase().includes(normalizedQuery),
    ),
  );
}
